import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import ImportDialog from "../components/ImportDialog";
import { useRouter } from "../../../hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";

interface Development {
  id: string;
  name: string;
}

interface Debtor {
  id: string;
  fullName: string;
  unitRef?: string;
  developmentId?: string;
}

interface Receipt {
  id: string;
  receiptNo: string;
  receiptDate: string;
  debtorId: string;
  debtor?: Debtor;
  developmentId?: string;
  amount: number;
  receiptType?: string;
  vatAmount?: number;
  cashbook?: string;
  reference?: string;
  notes?: string;
  createdAt?: string;
  paymentType?: string;
}

const ReceiptsList: React.FC = () => {
  const router = useRouter();
  const [searchParams] = useSearchParams();
  const receiptType = searchParams.get("type") || "payment";
  const paymentTypeFilter = searchParams.get("paymentType") || "";
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDevelopment, setSelectedDevelopment] = useState<string>("");
  const [sortField, setSortField] = useState<keyof Receipt>("receiptDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [formData, setFormData] = useState({
    debtorId: "",
    developmentId: "",
    receiptNo: "",
    receiptDate: new Date().toISOString().split("T")[0],
    amount: 0,
    vatAmount: 0,
    paymentType:
      receiptType === "vat" ? "vat" : paymentTypeFilter || "installment",
    cashbook: "bank",
    reference: "",
    notes: "",
  });

  // Summary stats
  const [stats, setStats] = useState({
    totalReceipts: 0,
    totalAmount: 0,
    thisMonth: 0,
    thisMonthAmount: 0,
  });

  useEffect(() => {
    fetchReceipts();
    fetchDevelopments();
    fetchDebtors();
  }, [selectedDevelopment, receiptType, paymentTypeFilter]);

  const fetchDevelopments = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/developments`);
      if (response.ok) {
        const data = await response.json();
        setDevelopments(data);
      }
    } catch (error) {
      console.error("Error fetching developments:", error);
    }
  };

  const fetchDebtors = async () => {
    try {
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/debtors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        setDebtors(await response.json());
      }
    } catch (error) {
      console.error("Error fetching debtors:", error);
    }
  };

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      let url = `${config.API_BASE_URL}/receipts?receiptType=${receiptType}`;
      if (paymentTypeFilter) {
        url += `&paymentType=${paymentTypeFilter}`;
      }
      if (selectedDevelopment) {
        url += `&developmentId=${selectedDevelopment}`;
      }
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setReceipts(data);
        calculateStats(data);
      }
    } catch (error) {
      console.error("Error fetching receipts:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: Receipt[]) => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const totalAmount = data.reduce((sum, r) => sum + (r.amount || 0), 0);
    const thisMonthReceipts = data.filter((r) => {
      const date = new Date(r.receiptDate);
      return date.getMonth() === thisMonth && date.getFullYear() === thisYear;
    });
    const thisMonthAmount = thisMonthReceipts.reduce(
      (sum, r) => sum + (r.amount || 0),
      0,
    );

    setStats({
      totalReceipts: data.length,
      totalAmount,
      thisMonth: thisMonthReceipts.length,
      thisMonthAmount,
    });
  };

  const handleSort = (field: keyof Receipt) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const filteredReceipts = receipts
    .filter(
      (receipt) =>
        receipt.receiptNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        receipt.debtor?.fullName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        receipt.debtor?.unitRef
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        receipt.reference?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      let aVal: any = a[sortField] ?? "";
      let bVal: any = b[sortField] ?? "";

      // Handle date sorting
      if (sortField === "receiptDate") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getPaymentMethodColor = (method?: string) => {
    switch (method?.toLowerCase()) {
      case "cash":
        return "success";
      case "bank":
      case "eft":
        return "primary";
      case "ecocash":
        return "warning";
      case "swipe":
        return "info";
      default:
        return "default";
    }
  };

  const resetForm = () => {
    setFormData({
      debtorId: "",
      developmentId: "",
      receiptNo: "",
      receiptDate: new Date().toISOString().split("T")[0],
      amount: 0,
      vatAmount: receiptType === "vat" ? 0 : 0,
      paymentType:
        receiptType === "vat" ? "vat" : paymentTypeFilter || "installment",
      cashbook: "bank",
      reference: "",
      notes: "",
    });
    setSaveError("");
  };

  const handleSavePayment = async () => {
    if (!formData.debtorId || !formData.receiptNo || !formData.receiptDate) {
      setSaveError("Debtor, receipt number, and receipt date are required.");
      return;
    }

    try {
      setSaving(true);
      setSaveError("");
      const token = localStorage.getItem("vms_access_token");
      const url = selectedReceipt
        ? `${config.API_BASE_URL}/receipts/${selectedReceipt.id}`
        : `${config.API_BASE_URL}/debtors/${formData.debtorId}/receipts`;
      const response = await fetch(url, {
        method: selectedReceipt ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          receiptType,
          paymentType: receiptType === "vat" ? "vat" : formData.paymentType,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save payment.");
      }

      setCreateDialogOpen(false);
      setSelectedReceipt(null);
      resetForm();
      fetchReceipts();
    } catch (error: any) {
      setSaveError(error.message || "Failed to save payment.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditReceipt = (receipt: Receipt) => {
    setSelectedReceipt(receipt);
    setFormData({
      debtorId: receipt.debtorId,
      developmentId: receipt.developmentId || "",
      receiptNo: receipt.receiptNo,
      receiptDate: receipt.receiptDate?.split("T")[0] || "",
      amount: receipt.amount || 0,
      vatAmount: receipt.vatAmount || 0,
      paymentType: receipt.paymentType || (receiptType === "vat" ? "vat" : "installment"),
      cashbook: receipt.cashbook || "bank",
      reference: receipt.reference || "",
      notes: receipt.notes || "",
    });
    setCreateDialogOpen(true);
  };

  const handleDeleteReceipt = async (receipt: Receipt) => {
    if (!window.confirm(`Delete payment ${receipt.receiptNo}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/receipts/${receipt.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchReceipts();
      }
    } catch (error) {
      console.error("Error deleting receipt:", error);
    }
  };

  return (
    <FinanceContainer>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {receiptType === "vat"
            ? "VAT Payments"
            : paymentTypeFilter === "interest"
              ? "Interest Payments"
              : "Instalment Payments"}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedReceipt(null);
              resetForm();
              setCreateDialogOpen(true);
            }}
          >
            {receiptType === "vat" ? "Capture VAT Payment" : "Capture Payment"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            {receiptType === "vat"
              ? "Bulk Import VAT Payments"
              : paymentTypeFilter === "interest"
                ? "Bulk Import Interest Payments"
                : "Bulk Import Instalment Payments"}
          </Button>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Receipts
                </Typography>
                <Typography variant="h4">{stats.totalReceipts}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h4" sx={{ color: "success.main" }}>
                  {formatCurrency(stats.totalAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Month Count
                </Typography>
                <Typography variant="h4">{stats.thisMonth}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  This Month Amount
                </Typography>
                <Typography variant="h4" sx={{ color: "primary.main" }}>
                  {formatCurrency(stats.thisMonthAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by receipt no, debtor name, unit ref, or reference..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setImportDialogOpen(true)}
                >
                  Import Receipts
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Development</InputLabel>
                <Select
                  value={selectedDevelopment}
                  onChange={(e) => setSelectedDevelopment(e.target.value)}
                  label="Development"
                >
                  <MenuItem value="">All Developments</MenuItem>
                  {developments.map((dev) => (
                    <MenuItem key={dev.id} value={dev.id}>
                      {dev.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Receipts Table */}
        <TableContainer component={Paper}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={300}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "receiptNo"}
                      direction={
                        sortField === "receiptNo" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("receiptNo")}
                    >
                      Receipt No
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "receiptDate"}
                      direction={
                        sortField === "receiptDate" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("receiptDate")}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Debtor</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "amount"}
                      direction={sortField === "amount" ? sortDirection : "asc"}
                      onClick={() => handleSort("amount")}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  {receiptType === "vat" && <TableCell>VAT Amount</TableCell>}
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReceipts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={receiptType === "vat" ? 10 : 9}
                      align="center"
                    >
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No receipts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReceipts.map((receipt) => (
                    <TableRow
                      key={receipt.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        router.push(`/finance/debtors/${receipt.debtorId}`)
                      }
                    >
                      <TableCell>
                        <Typography fontWeight={500}>
                          {receipt.receiptNo}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(receipt.receiptDate)}</TableCell>
                      <TableCell>
                        {receipt.debtor?.fullName || receipt.debtorId}
                      </TableCell>
                      <TableCell>{receipt.debtor?.unitRef || "-"}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{ color: "success.main", fontWeight: 500 }}
                        >
                          {formatCurrency(receipt.amount)}
                        </Typography>
                      </TableCell>
                      {receiptType === "vat" && (
                        <TableCell>
                          <Typography
                            sx={{ color: "info.main", fontWeight: 500 }}
                          >
                            {formatCurrency(receipt.vatAmount || 0)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={receipt.cashbook || "Unknown"}
                          size="small"
                          color={getPaymentMethodColor(receipt.cashbook)}
                        />
                      </TableCell>
                      <TableCell>{receipt.reference || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={receipt.paymentType || receipt.receiptType || "other"}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditReceipt(receipt);
                          }}
                          title="Edit Payment"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteReceipt(receipt);
                          }}
                          title="Delete Payment"
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            router.push(`/finance/debtors/${receipt.debtorId}`);
                          }}
                          title="View Debtor"
                        >
                          <ViewIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Import Dialog */}
        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          type="receipts"
          onImportComplete={fetchReceipts}
        />

        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedReceipt
              ? receiptType === "vat"
                ? "Edit VAT Payment"
                : "Edit Payment"
              : receiptType === "vat"
                ? "Capture VAT Payment"
                : "Capture Payment"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {saveError ? (
                <Grid item xs={12}>
                  <Alert severity="error">{saveError}</Alert>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Debtor</InputLabel>
                  <Select
                    value={formData.debtorId}
                    label="Debtor"
                    onChange={(e) => {
                      const debtor = debtors.find((item) => item.id === e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        debtorId: e.target.value,
                        developmentId: debtor?.developmentId || "",
                      }));
                    }}
                  >
                    {debtors.map((debtor) => (
                      <MenuItem key={debtor.id} value={debtor.id}>
                        {debtor.fullName} {debtor.unitRef ? `(${debtor.unitRef})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Receipt Number"
                  value={formData.receiptNo}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, receiptNo: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Receipt Date"
                  value={formData.receiptDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, receiptDate: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Amount"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, amount: Number(e.target.value || 0) }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="VAT Amount"
                  value={formData.vatAmount}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, vatAmount: Number(e.target.value || 0) }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Category</InputLabel>
                  <Select
                    value={formData.paymentType}
                    label="Payment Category"
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, paymentType: e.target.value }))
                    }
                    disabled={receiptType === "vat"}
                  >
                    <MenuItem value="installment">Installment</MenuItem>
                    <MenuItem value="interest">Interest</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Cashbook</InputLabel>
                  <Select
                    value={formData.cashbook}
                    label="Cashbook"
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, cashbook: e.target.value }))
                    }
                  >
                    <MenuItem value="bank">Bank</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="ecocash">EcoCash</MenuItem>
                    <MenuItem value="swipe">Swipe</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reference"
                  value={formData.reference}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, reference: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSavePayment} disabled={saving}>
              {saving ? "Saving..." : selectedReceipt ? "Update Payment" : "Save Payment"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </FinanceContainer>
  );
};

export default ReceiptsList;
